import type { Ref } from "react";
import type { InputWidth } from "./inputWidth";
import { inputWidthClasses } from "./inputWidth";
import { useMarkDialogDirty } from "../Dialog/FormDirtyContext";
import { useIsFormItemError } from "../FormLayout/FormItemErrorProvider";
import { useControlPresentation } from "./Internal/useControlPresentation";

interface TextAreaProps {
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  /**
   * Forwarded to the underlying <textarea>. Required when callers need to
   * read selectionStart/selectionEnd (e.g. inserting a markdown snippet at
   * the cursor) or imperatively focus the field after a side-effect.
   */
  ref?: Ref<HTMLTextAreaElement>;
  /**
   * Stretch the textarea to fill its flex parent's available height instead
   * of sizing to `rows`. Use inside a `flex flex-col` container whose own
   * height is constrained (e.g. a fullHeight Dialog body).
   */
  grow?: boolean;
  /**
   * Auto-grow with content and allow manual vertical resizing (drag handle).
   * Height follows the text as the user types, with no ceiling. `rows` sets
   * the starting height and stays the floor, so an expandable field is never
   * shorter than a plain one. Use for free-form note fields where operators
   * write multi-line text.
   *
   * Auto-grow uses CSS `field-sizing: content` and is intentionally
   * CSS-only: in browsers without support (Firefox) only the manual resize
   * handle works — almost nobody on the team uses them, so no JS fallback.
   */
  expandable?: boolean;
  /**
   * Pick a width from the shared control scale so the field lines up with the
   * text inputs around it. Defaults to filling the container. Always capped at
   * the container's width, so a fixed size still shrinks on a narrow screen.
   */
  width?: InputWidth | undefined;
  /** Forwarded to the underlying <textarea> for test selection. */
  "data-testid"?: string | undefined;
}

export function TextArea({
  value,
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
  placeholder,
  rows = 3,
  disabled,
  autoFocus,
  ref,
  grow,
  expandable,
  width,
  "data-testid": dataTestid,
}: TextAreaProps) {
  const markDirty = useMarkDialogDirty();
  const isError = useIsFormItemError();
  // A textarea is always a box (multi-line can't be an underline), but it still
  // has to sit next to underlined fields in the spacious customer flows. Under
  // the "underlined" presentation we mirror Cargo's textarea — bold 20px value,
  // thin border that thickens to 2px brand on hover/focus (padding shifts to
  // absorb the extra pixel so the box doesn't jiggle), and minimal rounding — so
  // it reads as part of the same family. The name is a slight misnomer (it's a
  // box, not an underline), but it keeps the presentation axis to two values.
  const presentation = useControlPresentation();
  const underlined = presentation === "underlined";
  // field-sizing-content makes the box track its content; resize-y adds the
  // manual drag handle (a manual drag sets an explicit height, which then
  // wins over the auto sizing — expected).
  const resizeClasses = expandable
    ? "field-sizing-content resize-y"
    : "resize-none";
  const widthClasses = width
    ? `${inputWidthClasses[width]} max-w-full`
    : "w-full";
  // Content sizing ignores `rows`, so an empty field would collapse to one
  // line. Keep `rows` as the floor so each caller's chosen height still holds.
  // 1lh is one line of this element's text; the constant is vertical padding
  // plus borders, which stays fixed across the underlined hover/focus states.
  const expandableMinHeight = `calc(${rows} * 1lh + ${underlined ? "1.375rem" : "1.125rem"})`;
  const boxedClasses = `${widthClasses} ${resizeClasses} rounded-md border ${isError ? "border-red" : "border-border-strong focus:border-brand"} bg-card px-3 py-2 text-sm text-foreground transition-colors placeholder:text-faint focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 ${grow ? "min-h-0 flex-1" : ""}`;
  const underlinedClasses = `${widthClasses} ${resizeClasses} rounded-sm border bg-transparent p-2.5 text-xl font-bold text-foreground transition-colors placeholder:font-normal placeholder:text-faint focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 ${
    isError
      ? "border-2 border-red p-[9px]"
      : "border-muted-foreground hover:border-2 hover:border-brand hover:p-[9px] focus:border-2 focus:border-brand focus:p-[9px]"
  } ${grow ? "min-h-0 flex-1" : ""}`;

  return (
    <textarea
      ref={ref}
      {...(dataTestid !== undefined && { "data-testid": dataTestid })}
      value={value}
      onChange={(e) => {
        markDirty?.();
        onChange?.(e);
      }}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
      placeholder={placeholder}
      rows={grow ? undefined : rows}
      disabled={disabled}
      autoFocus={autoFocus}
      {...(expandable === true && {
        style: { minHeight: expandableMinHeight },
      })}
      className={underlined ? underlinedClasses : boxedClasses}
    />
  );
}
